<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Resources"
      subtitle="Manage downloadable and viewable resources"
      icon="fas fa-folder-open"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search title or description"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Category"
          label-for="filterCategory"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.category"
            input-id="filterCategory"
            label="category_name"
            :options="categoryOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy || fetchingCategories"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Status"
          label-for="filterActive"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.active"
            input-id="filterActive"
            label="name"
            :options="activeOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Resource
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="info"
            :disabled="tableData.busy"
            @click.prevent="openFile(item)"
          >
            View
          </BButton>
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(description)="{ item }">
        <span :title="item.description">{{ trimText(item.description, 80) }}</span>
      </template>

      <template #cell(category)="{ item }">
        {{ item.category?.category_name || 'N/A' }}
      </template>

      <template #cell(is_active)="{ item }">
        <BBadge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Resource' : 'Add Resource'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="title"
                name="title"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Title"
                  label-for="fTitle"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fTitle"
                    v-model="formData.title"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="category_id"
                name="category"
                rules="required"
              >
                <BFormGroup
                  label="Category"
                  label-for="fCategory"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selectedCategory"
                    input-id="fCategory"
                    label="category_name"
                    :options="categoryOptions"
                    :loading="fetchingCategories"
                    :disabled="state.busy || fetchingCategories"
                    append-to-body
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="description"
                name="description"
                rules="required"
              >
                <BFormGroup
                  label="Description"
                  label-for="fDescription"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fDescription"
                    v-model="formData.description"
                    rows="4"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="file"
                name="file"
                :rules="state.editing ? '' : 'required'"
              >
                <BFormGroup
                  label="File"
                  label-for="fFile"
                  label-class="font-weight-bolder"
                  :description="state.editing ? 'Upload only if you want to replace the existing file.' : ''"
                  class="mb-2"
                >
                  <BFormFile
                    id="fFile"
                    v-model="formData.file"
                    :disabled="state.busy"
                    browse-text="Browse"
                    placeholder="Choose a file"
                    drop-placeholder="Drop file here"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback d-block"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formData.is_active"
                  switch
                  value="1"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRoles from '@/mixins/shared/roles'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminResource, SSharedList } from '@/services'

export default {
  name: 'VAdminResources',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter, MRoles],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Resources' }),

  data () {
    return {
      state: { busy: false, editing: false },
      categoryOptions: [],
      selectedCategory: null,
      fetchingCategories: false,
      activeOptions: [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' }
      ],
      formData: {
        id: '',
        title: '',
        description: '',
        category_id: '',
        file: null,
        is_active: 1
      },
      tableData: {
        busy: false,
        selected: {
          category: null,
          active: null
        },
        filter: {
          search: '',
          category: '',
          is_active: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'updated_at',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-150px' },
          { mobile: 1, key: 'title', class: 'min-w-200px' },
          { mobile: 2, key: 'description', class: 'min-w-300px' },
          { mobile: 3, key: 'category', label: 'Category', class: 'min-w-180px' },
          { mobile: 4, key: 'is_active', class: 'text-center' },
          { mobile: 5, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    selectedCategory (value) {
      this.formData.category_id = value?.id || ''
    },

    'tableData.selected.category' (category) {
      this.tableData.filter.category = category?.id || ''
    },

    'tableData.selected.active' (active) {
      this.tableData.filter.is_active = active !== null && active !== undefined ? active.id : ''
    }
  },

  mounted () {
    this.fetchCategories()
  },

  methods: {
    trimText (text, length = 80) {
      if (!text) return ''
      return text.length > length ? `${text.slice(0, length)}...` : text
    },

    async fetchCategories () {
      this.fetchingCategories = true
      return SSharedList.getActiveResourceCategories({ per_page: 500, sort: 'name', sort_desc: false }).then(({ data: response }) => {
        const payload = response?.data || response
        this.categoryOptions = payload || []
      }).catch(() => {
        this.categoryOptions = []
      }).finally(() => {
        this.fetchingCategories = false
      })
    },

    buildFormDataPayload () {
      const data = new FormData()
      data.append('title', this.formData.title)
      data.append('description', this.formData.description)
      data.append('category_id', this.formData.category_id)
      data.append('is_active', this.formData.is_active)

      if (this.formData.file) {
        data.append('file', this.formData.file)
      }

      if (this.state.editing) {
        data.append('_method', 'PUT')
      }

      return data
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        category: filter.category,
        is_active: filter.is_active
      }

      return SAdminResource.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    createItem () {
      this.state.editing = false
      this.formData = { id: '', title: '', description: '', category_id: '', file: null, is_active: 1 }
      this.selectedCategory = null
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.formData = {
        id: item.id,
        title: item.title,
        description: item.description,
        category_id: item.category_id,
        file: null,
        is_active: item.is_active ? 1 : 0
      }
      this.selectedCategory = this.categoryOptions.find(category => Number(category.id) === Number(item.category_id)) || null
      this.$refs.modalForm.show()
    },

    async openFile (item) {
      this.state.busy = true
      SAdminResource.viewFile(item.id).then(({ data, headers }) => {
        const mimeType = headers['content-type'] || 'application/octet-stream'
        const blob = new Blob([data], { type: mimeType })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      }).catch(error => {
        this.swalError(error.response?.data?.message || 'Unable to open file')
      }).finally(() => {
        this.state.busy = false
      })
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async validForm => {
        if (!validForm) return this.swalInvalid()

        if (!this.state.editing && !this.formData.file) {
          this.swalError('File is required when creating a resource')
          return
        }

        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this resource?</h6>' : '<h6>Create this resource?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true
            const payload = this.buildFormDataPayload()

            const action = this.state.editing
              ? SAdminResource.update(this.formData.id, payload)
              : SAdminResource.store(payload)

            return action.then(() => {
              this.$refs.modalForm.hide()
              this.$refs.tableData.refetch()
              this.swalSuccess(`Resource ${this.state.editing ? 'updated' : 'created'} successfully`)
            }).catch(({ message }) => {
              if (message?.errors) {
                this.$refs.formData.setErrors(message.errors)
              }
            }).finally(() => {
              this.state.busy = false
            })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete resource',
        text: `Are you sure you want to delete "${item.title}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        SAdminResource.destroy(item.id).then(() => {
          this.swalSuccess('Resource deleted successfully')
          this.$refs.tableData.refetch()
        }).catch(error => {
          this.swalError(error.response?.data?.message || 'Failed to delete resource')
        }).finally(() => {
          this.state.busy = false
        })
      }
    }
  }
}
</script>
